// Cost of a TMS file when an invoice is cut from it.
//
// A house file (H: RHFL / OHFL / AHFL) carries its own costs, and the master
// file (M: RMBL / OMBL / AMBL) it is consolidated into carries the shared
// linehaul / freight costs. The invoice's total cost must be BOTH sides added
// together -- previously only the first non-zero H field was used, and the M
// side was only used as a fallback for a single-shipment master.
//
// Each part keeps its own currency; the invoice page converts and sums them.
// The master's cost is split equally across the house files linked to it, so
// invoicing every H of one M never counts the master cost more than once.

const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const text = (value) => (typeof value === "string" ? value.trim() : "");

const part = (label, amount, currency) => ({
    label,
    amount: num(amount),
    currency: text(currency) || "EUR",
});

const pricingOf = (mblAttr, shipmentId) => {
    const pricing = mblAttr?.shipmentPricing;
    if (!pricing || !shipmentId) return null;

    return pricing instanceof Map ? pricing.get(shipmentId) : pricing[shipmentId];
};

// Own cost of the house file (what is entered on the shipment itself).
const getHouseParts = ({ sourceType, row, mblAttr, shipmentId }) => {
    const fallbackCurrency =
        text(row?.buyingRateCurrency) ||
        text(row?.pickupRateCurrency) ||
        text(row?.deliveryRateCurrency) ||
        text(mblAttr?.financialSummary?.totalCurrency) ||
        "EUR";

    if (sourceType === "road") {
        // The RMBL pricing table holds the per-shipment cost the operator
        // confirmed for this house file; otherwise fall back to the shipment's
        // own rate fields -- the same formula ConnectMBL uses.
        const pricing = pricingOf(mblAttr, shipmentId);

        if (pricing && pricing.cost !== undefined && pricing.cost !== null) {
            return [
                part(
                    "Shipment cost",
                    pricing.cost,
                    pricing.costCurrency || fallbackCurrency,
                ),
            ];
        }

        return [
            part("Buying rate", row?.buyingRate, row?.buyingRateCurrency || fallbackCurrency),
            part("Pickup", row?.pickupRate, row?.pickupRateCurrency || fallbackCurrency),
            part("Delivery", row?.deliveryRate, row?.deliveryRateCurrency || fallbackCurrency),
            part(
                "Export customs",
                row?.exportCustomsRate,
                row?.exportCustomsRateCurrency || fallbackCurrency,
            ),
            part(
                "Import customs",
                row?.importCustomsRate,
                row?.importCustomsRateCurrency || fallbackCurrency,
            ),
        ];
    }

    // Ocean / air: the house file's own pickup and delivery legs.
    return [
        part("Pickup", row?.pickupRate, row?.pickupRateCurrency || fallbackCurrency),
        part("Delivery", row?.deliveryRate, row?.deliveryRateCurrency || fallbackCurrency),
    ];
};

// Shared cost of the master file (whole master, before it is split).
const getMasterParts = ({ sourceType, mblAttr }) => {
    if (!mblAttr) return [];

    const summary = mblAttr.financialSummary || {};
    const freight = mblAttr.freightCharges || {};
    const totalCurrency =
        text(summary.totalCurrency) || text(freight.totalCurrency) || "EUR";

    if (sourceType === "road") {
        const linehaul = mblAttr.linehaulDetails || {};
        const converted = num(summary.convertedLinehaulCost);

        return [
            converted > 0
                ? part("Linehaul", converted, totalCurrency)
                : part("Linehaul", linehaul.cost, linehaul.currency || totalCurrency),
            part("Buying rate", summary.buyingRate, totalCurrency),
            num(summary.convertedHandlingCost) > 0
                ? part("Handling", summary.convertedHandlingCost, totalCurrency)
                : part("Handling", mblAttr.handlingDetails?.cost, mblAttr.handlingDetails?.currency || totalCurrency),
        ];
    }

    const mainLeg = sourceType === "air" ? "airFreight" : "oceanFreight";
    const mainLabel = sourceType === "air" ? "Air freight" : "Ocean freight";
    const pick = (key, summaryKey) => {
        const charge = freight[key] || {};

        return num(charge.total) > 0 ? num(charge.total) : num(summary[summaryKey]);
    };

    return [
        part("Origin handling (OTHC)", pick("othc", "othcTotal"), totalCurrency),
        part(mainLabel, pick(mainLeg, `${mainLeg}Total`), totalCurrency),
        part("Destination handling (DTHC)", pick("dthc", "dthcTotal"), totalCurrency),
        part("Handling", pick("handling", "handlingTotal"), totalCurrency),
    ];
};

export const buildCostParts = ({
    sourceType,
    row,
    mblAttr,
    shipmentId,
    shipmentCount,
    masterNo,
}) => {
    const count = Math.max(Number(shipmentCount) || 1, 1);
    const houseParts = getHouseParts({ sourceType, row, mblAttr, shipmentId }).filter(
        (item) => item.amount > 0,
    );
    const masterParts = getMasterParts({ sourceType, mblAttr })
        .filter((item) => item.amount > 0)
        .map((item) => ({
            ...item,
            amount: item.amount / count,
            fullAmount: item.amount,
        }));

    return {
        houseCostParts: houseParts,
        masterCostParts: masterParts,
        masterNo: masterNo || "",
        masterShipmentCount: count,
    };
};

// Parts -> one amount in `currency`. `toRate(code)` resolves a currency's value
// in a common base (TRY); parts already in `currency` need no rate at all.
export const sumCostParts = async (parts, currency, toRate) => {
    const target = text(currency).toUpperCase();
    const rateCache = {};
    const rateOf = async (code) => {
        if (!(code in rateCache)) rateCache[code] = Number(await toRate(code)) || 1;
        return rateCache[code];
    };

    let total = 0;

    for (const item of parts || []) {
        const from = text(item.currency).toUpperCase();

        if (!target || !from || from === target) {
            total += num(item.amount);
            continue;
        }

        total += (num(item.amount) * (await rateOf(from))) / (await rateOf(target));
    }

    return Math.round(total * 100) / 100;
};
